import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ExternalLink, Building2, Mail, Phone, MapPin, FileText, User, Layers, Tag, ShieldCheck,
  Server, Target, Info, Shuffle, Ruler, Landmark, Wrench, Send, Loader2
} from "lucide-react";
import { toast } from "sonner";
import api, { safeExternalUrl } from "@/lib/api";

const CMS_COLORS = {
  Drupal: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  WordPress: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  TYPO3: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  Joomla: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  Shopify: "bg-pink-500/15 text-pink-400 border-pink-500/30",
};

const TECH_CAT_LABELS = {
  cms: "CMS", ecommerce: "E-Commerce", framework: "Framework",
  js_library: "JS Library", analytics: "Analytics", marketing: "Marketing",
  cdn: "CDN/Hosting", crm: "CRM", payments: "Payments", live_chat: "Live Chat",
  tag_manager: "Tag Manager", search: "Search", build: "Build", programming: "Language",
};

const SUPPORT = {
  eol: { text: "ohne Sicherheitsupdates", cls: "text-red-400" },
  eol_soon: { text: "Support endet bald", cls: "text-amber-400" },
  supported: { text: "unterstützt", cls: "text-emerald-400" },
  unknown: { text: "unbekannt", cls: "text-zinc-500" },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : null);
const fmtEur = (v) => (v != null ? `${Number(v).toLocaleString("de-DE")} €` : null);

const Section = ({ icon: Icon, title, children }) => (
  <div className="px-5 py-3 border-b border-zinc-800">
    <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
      <Icon className="w-3 h-3" /> {title}
    </p>
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, mono = false, className = "" }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-3 py-1.5">
      <Icon className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5">{label}</p>
        <p className={`text-sm text-zinc-200 break-words ${mono ? "font-mono text-xs" : ""} ${className}`}>{value}</p>
      </div>
    </div>
  );
};

const Tile = ({ label, value, className = "" }) => (
  <div className="bg-zinc-800/40 rounded-sm p-2 min-w-0">
    <p className="text-[9px] text-zinc-500 uppercase">{label}</p>
    <p className={`text-sm text-white truncate ${className}`} title={typeof value === "string" ? value : undefined}>{value ?? "—"}</p>
  </div>
);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((i) => (
      <span key={i} className="px-2 py-0.5 rounded-sm bg-zinc-800/80 text-[10px] text-zinc-300 border border-zinc-700/50">{i}</span>
    ))}
  </div>
);

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const defaultAssigneeEmail = (team, username) => {
  if (!team?.length) return "";
  const me = team.find((m) => (m.email || "").split("@")[0].toLowerCase() === (username || "").toLowerCase());
  return (me || team[0]).email || "";
};

const AssigneeSelect = ({ team, value, onChange }) => {
  if (!team?.length) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="h-7 bg-zinc-950 border-zinc-800 text-[11px] rounded-sm border px-2 text-zinc-300">
      {team.map((m) => (
        <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
      ))}
    </select>
  );
};

export const DomainDetailDialog = ({ domain, onClose, coreLinkEnabled = false, canWrite = false, coreTeam = [], currentUsername = "", lastAssignee = "", onAssigneeChange = () => {}, onCoreLinkChanged, onOpened }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [assignee, setAssignee] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/domains/${encodeURIComponent(domain)}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => { load(); }, [load]);

  // Record the open server-side (per user); the table marks the row without reloading.
  const onOpenedRef = useRef(onOpened);
  useEffect(() => { onOpenedRef.current = onOpened; }, [onOpened]);
  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    api.post(`/domains/${encodeURIComponent(domain)}/opened`)
      .then((res) => { if (!cancelled) onOpenedRef.current?.(domain, res.data.opened_at); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [domain]);

  // Default the assignee to this user's last pick (if still on the team), else the mapped Core person.
  useEffect(() => {
    if (!coreTeam?.length) return;
    const preferred = lastAssignee && coreTeam.some((m) => m.email === lastAssignee) ? lastAssignee : "";
    const fallback = preferred || defaultAssigneeEmail(coreTeam, currentUsername);
    if (!assignee) setAssignee(fallback);
  }, [coreTeam, currentUsername, assignee, lastAssignee]);

  const handleSendToCore = async (mode) => {
    setSending(true);
    try {
      const res = await api.post(`/domains/${encodeURIComponent(domain)}/send-to-core`, { mode, assignee_email: assignee || undefined });
      if (res.data.status === "exists") toast.warning("Bereits in Core vorhanden.");
      else if (res.data.status === "filled") toast.success("Leere Felder in Core ergänzt.");
      else toast.success("An Core gesendet.");
      await load();
      onCoreLinkChanged?.(domain);
    } catch (err) {
      toast.error(err.response?.data?.detail || "An Core senden fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const tech = data?.tech_stack || {};
  const dr = data?.drupal;
  const sv = data?.server;
  const hs = data?.hosting;
  const ng = data?.neglect;
  const lead = data?.lead;
  const cr = data?.company_register;
  const hr = data?.handelsregister;
  const pj = data?.project;
  const site = safeExternalUrl(data?.final_url || `https://${domain}`);
  const summary = data?.company_summary || data?.summary_meta?.description;
  const link = data?.core_link;
  const coreUrl = safeExternalUrl(link?.lead_url);

  return (
    <Dialog open={!!domain} onOpenChange={() => onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[88vh] overflow-y-auto p-0" data-testid="domain-detail-dialog">
        {loading && <p className="text-sm text-zinc-500 py-12 text-center">Lädt…</p>}

        {data && (
          <>
            <div className="p-5 border-b border-zinc-800">
              <DialogHeader className="mb-3">
                <DialogTitle className="text-lg font-bold text-white font-mono flex items-center gap-2">
                  {domain}
                  {site && (
                    <a href={site} target="_blank" rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300" data-testid="detail-visit-link">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs text-zinc-500">{data.final_url || `https://${domain}`}</DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                {data.cms && data.cms !== "unknown" && (
                  <Badge className={`text-[10px] ${CMS_COLORS[data.cms] || "bg-zinc-700/50 text-zinc-300 border-zinc-600"}`}>
                    {data.cms_major || data.cms}
                  </Badge>
                )}
                {data.branch && data.branch !== "Other" && (
                  <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/25 text-[10px]">{data.branch}</Badge>
                )}
                <Badge className={`text-[10px] ${data.crawl_status === "done" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>
                  {data.crawl_status === "done" ? "live" : data.crawl_status}
                </Badge>
                {data.redirect_target && (
                  <Badge className="bg-sky-500/15 text-sky-400 border-sky-500/30 text-[10px]">→ {data.redirect_target}</Badge>
                )}
                {data.status_code && (
                  <Badge className={`text-[10px] ${data.status_code === 200 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" : "bg-amber-500/10 text-amber-400 border-amber-500/25"}`}>
                    HTTP {data.status_code}
                  </Badge>
                )}
              </div>
            </div>

            {coreLinkEnabled && (link || canWrite) && (
              <div className="px-5 py-3 border-b border-zinc-800">
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
                  <Send className="w-3 h-3" /> Core
                </p>
                {!link && canWrite && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" disabled={sending}
                            onClick={() => handleSendToCore("create")}>
                      {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Send to Core
                    </Button>
                    <AssigneeSelect team={coreTeam} value={assignee} onChange={(email) => { setAssignee(email); onAssigneeChange(email); }} />
                  </div>
                )}
                {!link && !canWrite && <p className="text-xs text-zinc-600">Nicht in Core.</p>}
                {link?.status === "active" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs text-zinc-300">
                      In Core · added by {link.created_by_name || capitalize(link.sent_by)} on {fmtDate(link.sent_at)}
                      {link.assigned_to_name && link.assigned_to_name.toLowerCase() !== (link.created_by_name || capitalize(link.sent_by)).toLowerCase()
                        ? ` · for ${link.assigned_to_name}` : ""}
                    </p>
                    {coreUrl && (
                      <a href={coreUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs">
                        <ExternalLink className="w-3 h-3" /> Open in Core
                      </a>
                    )}
                  </div>
                )}
                {link?.status === "exists" && (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-400">Already in Core (added by {link.created_by_name || "?"} on {fmtDate(link.created_at)})</p>
                    <div className="flex items-center gap-2">
                      {coreUrl && (
                        <a href={coreUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs">
                          <ExternalLink className="w-3 h-3" /> Open in Core
                        </a>
                      )}
                      {canWrite && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm border-zinc-700 text-zinc-300"
                                disabled={sending} onClick={() => handleSendToCore("fill")}>
                          {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Fill empty fields
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {link?.status === "archived" && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-zinc-400">Archived in Core</p>
                    {coreUrl && (
                      <a href={coreUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs">
                        <ExternalLink className="w-3 h-3" /> Open in Core
                      </a>
                    )}
                  </div>
                )}
                {link?.status === "missing" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs text-red-400">Removed from Core</p>
                    {canWrite && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm border-zinc-700 text-zinc-300"
                              disabled={sending} onClick={() => handleSendToCore("create")}>
                        {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Send again
                      </Button>
                    )}
                    {canWrite && <AssigneeSelect team={coreTeam} value={assignee} onChange={(email) => { setAssignee(email); onAssigneeChange(email); }} />}
                  </div>
                )}
              </div>
            )}

            {lead && (
              <Section icon={Target} title={`Lead-Score ${lead.score}/100`}>
                {lead.opener && <p className="text-sm text-white mb-2">„{lead.opener}“</p>}
                <ul className="space-y-0.5">
                  {lead.reasons.map((r) => <li key={r} className="text-xs text-zinc-400">• {r}</li>)}
                </ul>
              </Section>
            )}

            {(summary || data.services?.length > 0) && (
              <Section icon={Info} title={data.company_summary ? "Was macht die Firma (Deep Research)" : "Beschreibung (Website)"}>
                {summary && <p className="text-sm text-zinc-200">{summary}</p>}
                {data.services?.length > 0 && <div className="mt-2"><Chips items={data.services} /></div>}
              </Section>
            )}

            {dr && (
              <Section icon={Tag} title="Drupal">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="Version" value={dr.version || (dr.version_minor ? `${dr.version_minor}.x` : dr.major || dr.major_hint || "?")} className="font-mono" />
                  <Tile label="Support" value={SUPPORT[dr.support_status || "unknown"].text} className={SUPPORT[dr.support_status || "unknown"].cls} />
                  <Tile label="Supportende" value={fmtDate(dr.eol_date)} />
                  <Tile label="Theme" value={dr.theme} className="font-mono" />
                </div>
                {dr.version_range && <p className="text-[10px] text-zinc-500 mt-2">Mögliche Versionen: {dr.version_range} (Quelle: {dr.version_source})</p>}
                {dr.headless && <p className="text-xs text-sky-400 mt-2">Headless-Drupal, Backend: {dr.headless_backend}</p>}
                {(dr.commerce || dr.webform || dr.drupal_cms || dr.multilingual) && (
                  <p className="text-xs text-zinc-400 mt-2">
                    {[dr.commerce && "Shop (Commerce)", dr.webform && "Webform", dr.multilingual && "mehrsprachig", dr.drupal_cms && "Drupal CMS"].filter(Boolean).join(" · ")}
                  </p>
                )}
                {dr.modules?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[9px] uppercase text-zinc-600 mb-1">Module ({dr.modules.length}{dr.custom_modules?.length ? ` + ${dr.custom_modules.length} eigene` : ""})</p>
                    <Chips items={dr.modules} />
                  </div>
                )}
              </Section>
            )}

            {(sv || hs) && (
              <Section icon={Server} title="Server & Hosting">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="Webserver" value={sv?.software ? `${sv.software}${sv.version ? ` ${sv.version}` : ""}` : null} className="font-mono" />
                  <Tile label="PHP" value={sv?.php_version || (sv?.php ? "ja (Version verborgen)" : null)} className="font-mono" />
                  <Tile label="PHP-Support" value={sv?.php_version ? SUPPORT[sv.php_support_status || "unknown"].text : null}
                        className={sv?.php_version ? SUPPORT[sv.php_support_status || "unknown"].cls : ""} />
                  <Tile label="Hoster" value={hs?.provider} />
                </div>
                {(sv?.os || sv?.panel || hs?.asn) && (
                  <p className="text-[10px] text-zinc-500 mt-2">
                    {[sv?.os && `OS: ${sv.os}`, sv?.panel && `Panel: ${sv.panel}`, hs?.asn && `AS${hs.asn} ${hs.as_name}`, hs?.ip && `IP ${hs.ip}`].filter(Boolean).join(" · ")}
                  </p>
                )}
              </Section>
            )}

            {ng && (
              <Section icon={Wrench} title="Zustand der Website">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="HTTPS / SSL" value={!ng.https ? "kein HTTPS" : ng.ssl_valid === false ? "Zertifikat ungültig" : "ok"}
                        className={!ng.https || ng.ssl_valid === false ? "text-red-400" : "text-emerald-400"} />
                  <Tile label="Mobil" value={ng.has_viewport ? "ja" : "nein"} className={ng.has_viewport ? "" : "text-amber-400"} />
                  <Tile label="Copyright" value={ng.copyright_year} />
                  <Tile label="jQuery" value={ng.jquery_version} className={ng.jquery_outdated ? "text-amber-400 font-mono" : "font-mono"} />
                </div>
                {data.creator && <p className="text-xs text-zinc-400 mt-2">Erstellt von: {data.creator.name || data.creator.domain} ({data.creator.domain})</p>}
              </Section>
            )}

            <Section icon={Building2} title="Kontakt / Impressum">
              <InfoRow icon={User} label="Inhaber / Geschäftsführung" value={data.owner_name} />
              <InfoRow icon={Building2} label="Firma" value={[data.company_name, data.legal_form && !data.company_name?.includes(data.legal_form) ? `(${data.legal_form})` : null].filter(Boolean).join(" ")} />
              <InfoRow icon={MapPin} label="Adresse" value={data.address} />
              <InfoRow icon={Phone} label="Telefon" value={data.phone} mono />
              <InfoRow icon={Mail} label="E-Mail" value={data.email} mono />
              <InfoRow icon={Landmark} label="Handelsregister" value={data.register_number ? `${data.register_type || ""} ${data.register_number}${data.register_court ? `, AG ${data.register_court}` : ""}` : null} mono />
              <InfoRow icon={FileText} label="USt-ID" value={data.ust_id} mono />
              {!data.owner_name && !data.company_name && !data.email && !data.phone && (
                <p className="text-[10px] text-zinc-600 py-1">Keine Impressumsdaten gefunden</p>
              )}
            </Section>

            {hr && hr.status === "found" && (
              <Section icon={Landmark} title={`Handelsregister (${hr.register?.register_type || ""} ${hr.register?.register_number || ""}, AG ${hr.register?.court || ""})`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="Status" value={hr.in_liquidation ? "in Liquidation" : hr.register_status} className={hr.in_liquidation ? "text-red-400" : ""} />
                  <Tile label="Rechtsform" value={hr.legal_form} />
                  <Tile label="Kapital" value={fmtEur(hr.share_capital_eur)} />
                  <Tile label="Letzte Eintragung" value={fmtDate(hr.last_entry_date)} />
                </div>
                {hr.representatives?.length > 0 && (
                  <p className="text-xs text-zinc-400 mt-2">
                    {hr.representatives.map((r) => `${r.name} (${r.role})`).join(" · ")}
                  </p>
                )}
                {hr.purpose && <p className="text-[11px] text-zinc-500 mt-2">Gegenstand: {hr.purpose}</p>}
              </Section>
            )}
            {hr && hr.status && hr.status !== "found" && (
              <Section icon={Landmark} title="Handelsregister">
                <p className="text-xs text-zinc-500">
                  {{ not_found: "Kein Eintrag gefunden.", ambiguous: "Mehrere mögliche Einträge – nicht eindeutig.",
                     skipped: "Übersprungen (kein Firmenname/keine Registernummer)." }[hr.status] || hr.status}
                </p>
              </Section>
            )}

            {data.company_size && (
              <Section icon={Building2} title={`Firmengröße (Schätzung): ${data.company_size.class} · Sicherheit ${({ low: "niedrig", medium: "mittel", high: "hoch" })[data.company_size.confidence] || ""}`}>
                <ul className="space-y-0.5">
                  {(data.company_size.reasons || []).map((r) => <li key={r} className="text-xs text-zinc-400">• {r}</li>)}
                </ul>
              </Section>
            )}

            {cr && (
              <Section icon={Landmark} title="Registerdaten (OpenRegister)">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="Größenklasse" value={cr.size_class} />
                  <Tile label="Mitarbeiter" value={cr.employees ?? (cr.employees_estimate ? `~${cr.employees_estimate.value} (Schätzung)` : null)} />
                  <Tile label="Umsatz" value={fmtEur(cr.revenue_eur) || (cr.revenue_estimate ? `~${fmtEur(cr.revenue_estimate.value)}` : null)} />
                  <Tile label="Gegründet" value={fmtDate(cr.incorporated_at)} />
                </div>
                {cr.directors?.length > 0 && <p className="text-xs text-zinc-400 mt-2">Geschäftsführung: {cr.directors.join(", ")}</p>}
              </Section>
            )}

            {pj && (
              <Section icon={Ruler} title="Projektgröße (Schätzung)">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile label="Klasse" value={pj.size_class || "?"} className="font-mono" />
                  <Tile label="Seiten" value={pj.pages_estimate != null ? pj.pages_estimate.toLocaleString("de-DE") : "keine Sitemap"} />
                  <Tile label="Sprachen" value={pj.languages} />
                  <Tile label="Shop / Login" value={[pj.has_shop && "Shop", pj.has_login && "Login"].filter(Boolean).join(", ") || "nein"} />
                </div>
              </Section>
            )}

            {data.redirect_sources?.length > 0 && (
              <Section icon={Shuffle} title="Weiterleitungen hierher">
                <Chips items={data.redirect_sources} />
              </Section>
            )}

            {Object.keys(tech).length > 0 && (
              <Section icon={Layers} title="Tech Stack">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(tech).map(([cat, items]) =>
                    items.map((item) => (
                      <span key={`${cat}-${item}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-800/80 text-[10px] text-zinc-300 border border-zinc-700/50">
                        <span className="text-zinc-500">{TECH_CAT_LABELS[cat] || cat}:</span> {item}
                      </span>
                    ))
                  )}
                </div>
              </Section>
            )}

            <Section icon={ShieldCheck} title="Crawl">
              <div className="grid grid-cols-3 gap-2">
                <Tile label="Zuletzt gecrawlt" value={data.last_crawled ? new Date(data.last_crawled).toLocaleDateString("de-DE") : "nie"} />
                <Tile label="Erkennung" value={data.confidence ? `${data.confidence}%` : "—"} className="font-mono" />
                <Tile label="Deep Research" value={data.research_status || "—"} />
              </div>
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
